import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Alert } from '@mui/material';
import { useSnackbar } from 'notistack';
import Spinner from '../Spinner.jsx';
import { requestTwoFactorChallenge } from '@accredo/fe-shared/services/twoFactorService.js';
import { Button } from '@accredo/fe-shared/theme/components/index.js';
import { dimensions } from '@accredo/fe-shared/theme/tokens/dimensions.js';

/**
 * Handles requesting a 2FA challenge after successful credential validation (v2 themed)
 */
export default function TwoFactorChallenge
({
    email,
    challengeToken,
    onChallengeRequested,
    onCancel
})
{
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleRequestChallenge = async () =>
    {
        setError(null);

        try
        {
            await requestTwoFactorChallenge(email, 'email', challengeToken,
            {
                setIsLoading,
                onSuccess: (data) =>
                {
                    enqueueSnackbar('Verifikationscode wurde an Ihre E-Mail-Adresse gesendet.', {
                        variant: 'success'
                    });
                    if(onChallengeRequested)
                    {
                        onChallengeRequested(data);
                    }
                },
                onError: (error_response) =>
                {
                    if(error_response?.response?.status === 401)
                    {
                        const error_message = 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.';
                        setError(error_message);
                        enqueueSnackbar(error_message, { variant: 'error' });
                        if(onCancel)
                        {
                            onCancel();
                        }
                        return;
                    }

                    const error_message = error_response?.response?.data?.message
                        || 'Fehler beim Anfordern des Verifikationscodes. Bitte versuchen Sie es erneut.';
                    setError(error_message);
                    enqueueSnackbar(error_message, { variant: 'error' });
                }
            });
        }
        catch(error_request)
        {
            console.error('Error requesting 2FA challenge:', error_request);
        }
    };

    if(isLoading)
    {
        return <Spinner />;
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: dimensions['xl'] }}>
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: dimensions['md'] }}>
                <Typography variant="h6">
                    Zwei-Faktor-Authentifizierung
                </Typography>
                <Typography color="text.secondary">
                    Zur Sicherheit Ihres Kontos ist eine Zwei-Faktor-Authentifizierung erforderlich. Klicken Sie auf "Code anfordern", um einen Verifikationscode an Ihre E-Mail-Adresse zu senden.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                E-Mail-Adresse: {email}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: dimensions['lg'] }}>
                <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleRequestChallenge}
                    disabled={isLoading}
                >
                    Code anfordern
                </Button>

                <Button
                    variant="tertiary"
                    fullWidth
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Abbrechen
                </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Der Verifikationscode ist 15 Minuten gültig.
            </Typography>
        </Box>
    );
}

TwoFactorChallenge.propTypes = {
    email: PropTypes.string.isRequired,
    challengeToken: PropTypes.string.isRequired,
    onChallengeRequested: PropTypes.func,
    onCancel: PropTypes.func
};
